import axios from 'axios';

import {
  AuditCarrierDetail,
  AuditCarrierSummary,
  Rate,
  transformRateForAPI,
  transformRateFromAPI,
} from '../models/Rate';
import { AddressType } from '../models/Terminal';
import carrierService from './carrierService';

const uri = '/rate';

const create = async (rate: Rate) => {
  const transformedRate = transformRateForAPI(rate);
  const { data } = await axios.post<Rate>(`${uri}`, transformedRate);
  return transformRateFromAPI(data);
};

const getAll = async (carrierId?: number) => {
  if (!carrierId) {
    const { data } = await axios.get<Rate[]>(`${uri}`);
    return data.map(transformRateFromAPI);
  }

  const rates = await carrierService.getRates(carrierId);
  return rates.map(transformRateFromAPI);
};

const update = async (rate: Rate) => {
  const transformedRate = transformRateForAPI(rate);
  const { data } = await axios.put<Rate>(`${uri}/${rate.id}`, transformedRate);
  return transformRateFromAPI(data);
};

const bulkAdd = async (rates: Rate[]) => {
  const transformedRates = rates.map(transformRateForAPI);
  const { data } = await axios.post<Rate[]>(
    `${uri}/bulk/add`,
    transformedRates
  );
  return data.map(transformRateFromAPI);
};

const bulkRemove = async (ids: number[]) => {
  await axios.post(`${uri}/bulk/remove`, ids);
};

const bulkReverse = async (ids: number[]) => {
  const { data } = await axios.post<Rate[]>(`${uri}/bulk/reverse`, ids);
  return data.map(transformRateFromAPI);
};

const bulkDuplicate = async (ids: number[]) => {
  const { data } = await axios.post<Rate[]>(`${uri}/bulk/duplicate`, ids);
  return data.map(transformRateFromAPI);
};

const bulkUpdate = async (rates: Rate[]) => {
  const transformedRates = rates.map(transformRateForAPI);
  const { data } = await axios.post<Rate[]>(
    `${uri}/bulk/update`,
    transformedRates
  );
  return data.map(transformRateFromAPI);
};

const bulkUpdatePreferred = async (ids: number[], preferred: number) => {
  const { data } = await axios.post(`${uri}/bulk/update/preferred`, {
    preferred,
    rates: ids,
  });
  return data;
};

const assignTerminals = async (
  ids: number[],
  terminals: number[],
  type: AddressType
) => {
  const { data } = await axios.post<Rate[]>(`${uri}/terminal`, {
    destinationTerminals: type === 'destination' ? terminals : null,
    originTerminals: type === 'origin' ? terminals : null,
    rates: ids,
  });
  return data.map(transformRateFromAPI);
};

const singleAssignTerminals = async (
  id: number,
  originTerminals: number[],
  destinationTerminals: number[]
) => {
  const { data } = await axios.post<Rate>(`${uri}/terminal/single`, {
    destinationTerminals,
    id,
    originTerminals,
  });
  return transformRateFromAPI(data);
};

const adjustBaseRates = async (
  ids: number[],
  adjustBy: number,
  adjustType: string
) => {
  const { data } = await axios.post<Rate[]>(`${uri}/bulk/baseadjust`, {
    amount: adjustBy,
    rates: ids,
    type: adjustType,
  });
  return data.map(transformRateFromAPI);
};

const getAuditSummary = async (): Promise<AuditCarrierSummary[]> => {
  const { data } = await axios.get<AuditCarrierSummary[]>(`${uri}/audit`);
  return data;
};

const getAuditByCarrier = async (
  carrierId: number
): Promise<AuditCarrierDetail> => {
  const { data } = await axios.get<AuditCarrierDetail>(
    `${uri}/audit/${carrierId}`
  );
  // transform rates within each duplicate group to be Rate objects
  return {
    ...data,
    duplicateGroups: data.duplicateGroups.map((group) => ({
      ...group,
      rates: group.rates.map(transformRateFromAPI),
    })),
  };
};

const updateCoordinates = async (rateId: number) => {
  const { data } = await axios.post<{ rate: any }>(
    `${uri}/${rateId}/update-coordinates`
  );
  const rate = data.rate ?? data;
  return transformRateFromAPI(rate);
};

export interface BulkUpdateCoordinatesResult {
  total: number;
  updated: number;
  skipped: number;
  failed: number;
  results: Array<{ id: number; coordsUpdated: boolean }>;
  errors: Array<{ id: number; error: string }>;
}

const bulkUpdateCoordinates = async (
  ids: number[]
): Promise<BulkUpdateCoordinatesResult> => {
  const { data } = await axios.post<BulkUpdateCoordinatesResult>(
    `${uri}/bulk/update-coordinates`,
    { ids }
  );
  return data;
};

const mergeDuplicateRates = async (
  carrierId: number,
  origin: string,
  destination: string,
  shipTypeId: number,
  destinationShipTypeId: number,
  modeId: number,
  selectedRateClasses?: { [classId: number]: number },
  selectedRateId?: number | null
): Promise<Rate> => {
  const body: Record<string, unknown> = {
    carrierId,
    destination,
    destinationShipTypeId,
    modeId,
    origin,
    selectedRateClasses,
    shipTypeId,
  };
  if (selectedRateId != null) {
    body.selectedRateId = selectedRateId;
  }
  const { data } = await axios.post<Rate>(`${uri}/audit/merge`, body);
  return transformRateFromAPI(data);
};

const apis = {
  adjustBaseRates,
  assignTerminals,
  bulkAdd,
  bulkDuplicate,
  bulkRemove,
  bulkReverse,
  bulkUpdate,
  bulkUpdateCoordinates,
  bulkUpdatePreferred,
  create,
  getAll,
  getAuditByCarrier,
  getAuditSummary,
  mergeDuplicateRates,
  singleAssignTerminals,
  update,
  updateCoordinates,
};

export default apis;
